import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  ClipboardCheck, Printer, Clock, Eye, X, FileText, CheckCircle,
  Plus, Trash2, Edit3, Save, Users, AlertTriangle, Layers, Loader2,
  FileType2,
} from 'lucide-react';
import { useGetClassesQuery, useGetPolesQuery } from '../../services/polesOptionsApi';
import { useGetAnneesAcademiquesQuery, useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import {
  useLazyGetDonneesPVQuery,
  useEnregistrerPVMutation,
  useGetHistoriquePVQuery,
  useGetMembresJuryQuery,
  useCreateMembreJuryMutation,
  useUpdateMembreJuryMutation,
  useDeleteMembreJuryMutation,
  useLazyGetDonneesBottinQuery,
  type DonneesPV,
  type MembreJury,
  type MembreJuryCreate,
  type DonneesBottin,
} from '../../services/pvDeliberationApi';
import PVPreview, { buildPVHTML, buildPVByModele, buildPVBatchHTML } from '../../components/PV/PVPreview';
import { PV_MODELES, type PVModele, type PVModeleOptions } from '../../components/PV/PVModeles';
import { buildBottinHTML } from '../../components/PV/BottinBuilder';
import BottinReinscriptionTab from '../../components/PV/BottinReinscriptionTab';
import { exportPVAsWord, buildPVFilename } from '../../components/PV/PVWordExport';

// ─── Types ──────────────────────────────────────────────────────────────────

type ActiveTab = 'generation' | 'bottin' | 'bottin_reinsc' | 'historique';
type SessionType = 'ordinaire' | 'rattrapage' | 'synthese';

const FONCTIONS: { value: string; label: string }[] = [
  { value: 'president', label: 'Président du Jury' },
  { value: 'vice_president', label: 'Vice-Président' },
  { value: 'responsable_pole', label: 'Responsable de Pôle' },
  { value: 'chef_departement', label: "Chef de Département / Resp. d'Option" },
  { value: 'scolarite', label: 'Représentant Scolarité' },
  { value: 'secretaire', label: 'Secrétaire de séance' },
  { value: 'membre', label: 'Membre' },
];

// ─── Composant principal ────────────────────────────────────────────────────

const PVDeliberation: React.FC = () => {
  // Onglets
  const [activeTab, setActiveTab] = useState<ActiveTab>('generation');

  // Paramètres PV
  const [anneeAcademique, setAnneeAcademique] = useState('2024-2025');
  const [semestre, setSemestre] = useState<1 | 2>(1);
  const [session, setSession] = useState<SessionType>('ordinaire');
  const [classeCode, setClasseCode] = useState('');
  const [dateDeliberation, setDateDeliberation] = useState('');
  const [modele, setModele] = useState<PVModele>('detaille');
  const [salleJury, setSalleJury] = useState('');

  // Champs spécifiques au modèle ministériel
  const [juryNumero, setJuryNumero] = useState('');
  const [noteServiceNumero, setNoteServiceNumero] = useState('');
  const [noteServiceDate, setNoteServiceDate] = useState('');
  const [ville, setVille] = useState('Pointe-Noire');
  const [nationaliteParDefaut, setNationaliteParDefaut] = useState('Congolaise');
  // Mois de la session d'examens — indépendant de la date de délibération.
  // Pré-rempli automatiquement depuis l'AnneeAcademique active selon la
  // session sélectionnée (ordinaire → date_debut_examens, rattrapage →
  // date_debut_rattrapage), modifiable à la main.
  const [moisSession, setMoisSession] = useState('');
  const [moisSessionTouched, setMoisSessionTouched] = useState(false);

  const pvOptions: PVModeleOptions = {
    salle: salleJury || undefined,
    juryNumero: juryNumero || undefined,
    noteServiceNumero: noteServiceNumero || undefined,
    noteServiceDate: noteServiceDate
      ? new Date(noteServiceDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
      : undefined,
    ville: ville || undefined,
    nationaliteParDefaut: nationaliteParDefaut || undefined,
    moisSession: moisSession || undefined,
  };

  // Données PV chargées
  const [pvData, setPvData] = useState<DonneesPV | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Jury form
  const [showJuryForm, setShowJuryForm] = useState(false);
  const [editingJury, setEditingJury] = useState<MembreJury | null>(null);
  const [juryForm, setJuryForm] = useState<MembreJuryCreate>({
    nom_complet: '', fonction: 'membre', titre_academique: '', pole: 0, ordre_affichage: 0, actif: true,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; nom: string } | null>(null);

  // Filtres historique
  const [histAnnee, setHistAnnee] = useState('');
  const [histSession, setHistSession] = useState('');
  const [histDateDebut, setHistDateDebut] = useState('');
  const [histDateFin, setHistDateFin] = useState('');

  // Chargement / erreurs
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [success, setSuccess] = useState('');

  // Mode batch : imprimer toutes les classes d'un filtre (pôle/niveau) en lot
  const [batchMode, setBatchMode] = useState(false);
  const [batchPole, setBatchPole] = useState<string>('');     // nom du pôle
  const [batchNiveau, setBatchNiveau] = useState<string>(''); // 'L1' | 'L2' | 'L3' | 'M1' | 'M2' | ''
  const [batchFormation, setBatchFormation] = useState<'' | 'FI' | 'CS'>('');
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number; classeEnCours?: string } | null>(null);

  // ── API ──
  const { data: classes = [] } = useGetClassesQuery({ actif: true });
  const { data: annees = [] } = useGetAnneesAcademiquesQuery({});
  // L'objet complet (avec date_debut_examens / date_debut_rattrapage) — la
  // liste ci-dessus est tronquée à libelle/actif. Utilisé pour le pré-remplissage
  // automatique du mois de session dans le modèle ministériel.
  const { data: anneeActiveFull } = useGetAnneeActiveQuery();
  const [fetchDonneesPV] = useLazyGetDonneesPVQuery();
  const [enregistrerPV] = useEnregistrerPVMutation();
  const { data: historique } = useGetHistoriquePVQuery({
    annee_academique: histAnnee || undefined,
    session: histSession || undefined,
    date_debut: histDateDebut || undefined,
    date_fin: histDateFin || undefined,
  });

  // Auto-pré-remplissage du mois de session depuis l'AnneeAcademique active :
  // - session ordinaire  → mois de `date_debut_examens`
  // - session rattrapage → mois de `date_debut_rattrapage`
  // L'utilisateur peut écraser la valeur (flag moisSessionTouched). On
  // récupère l'AnneeAcademique courante via l'objet `annees` (déjà chargé).
  const moisFr = useMemo(() => [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
  ], []);
  // Pour le mois de session, on utilise l'année active complète si elle
  // correspond à l'année sélectionnée. (Si l'utilisateur regarde une année
  // archivée, on n'a pas les dates ; il devra saisir le mois à la main.)
  const anneeFull = anneeActiveFull && anneeActiveFull.libelle === anneeAcademique ? anneeActiveFull : null;
  useEffect(() => {
    if (moisSessionTouched) return;
    const dateRef = session === 'rattrapage'
      ? anneeFull?.date_debut_rattrapage
      : anneeFull?.date_debut_examens;
    if (dateRef && /^\d{4}-\d{2}-\d{2}/.test(dateRef)) {
      const monthIdx = parseInt(dateRef.slice(5, 7), 10) - 1;
      if (monthIdx >= 0 && monthIdx < 12) {
        setMoisSession(moisFr[monthIdx]);
        return;
      }
    }
    setMoisSession('');
  }, [anneeFull, session, moisFr, moisSessionTouched]);

  // Auto-switch du modèle selon la session choisie.
  //  - session=synthese → forcer synthese_bi_session (seul modèle compatible)
  //  - session=ordinaire/rattrapage → si le modèle actif est synthese_bi_session,
  //    revenir au ministériel par défaut.
  useEffect(() => {
    if (session === 'synthese' && modele !== 'synthese_bi_session') {
      setModele('synthese_bi_session');
    } else if (session !== 'synthese' && modele === 'synthese_bi_session') {
      setModele('ministeriel');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Trouver le pôle de la classe sélectionnée (via pole_nom → id)
  const { data: poles = [] } = useGetPolesQuery({});
  const selectedClasse = classes.find(c => c.code === classeCode);
  const poleId = selectedClasse ? (poles.find(p => p.nom === selectedClasse.pole_nom)?.id ?? null) : null;

  const { data: membresJury = [] } = useGetMembresJuryQuery(
    { pole: poleId ?? undefined, actif: true },
    { skip: !poleId }
  );
  const [createMembreJury] = useCreateMembreJuryMutation();
  const [updateMembreJury] = useUpdateMembreJuryMutation();
  const [deleteMembreJury] = useDeleteMembreJuryMutation();

  // ── Charger les données PV ──
  const handleCharger = useCallback(async () => {
    if (!classeCode) { setErreur('Veuillez sélectionner une classe.'); return; }
    setErreur('');
    setSuccess('');
    setLoading(true);
    try {
      const result = await fetchDonneesPV({
        classe_code: classeCode,
        semestre,
        annee_academique: anneeAcademique,
        session,
      });
      if (result.data) {
        setPvData(result.data);
        setSuccess(`Données chargées : ${result.data.etudiants?.length ?? 0} étudiants, ${result.data.ues_structure?.length ?? 0} UEs.`);
      } else {
        setErreur('Aucune donnée trouvée pour cette combinaison.');
      }
    } catch {
      setErreur('Erreur lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  }, [classeCode, semestre, anneeAcademique, session, fetchDonneesPV]);

  // ── Prévisualiser ──
  const handlePrevisualiser = async () => {
    if (!pvData) {
      await handleCharger();
      setShowPreview(true);
    } else {
      setShowPreview(true);
    }
  };

  // ── Imprimer ──
  const handleImprimer = async () => {
    let data = pvData;
    if (!data) {
      if (!classeCode) { setErreur('Veuillez sélectionner une classe.'); return; }
      setLoading(true);
      try {
        const result = await fetchDonneesPV({
          classe_code: classeCode,
          semestre,
          annee_academique: anneeAcademique,
          session,
        });
        if (result.data) {
          data = result.data;
          setPvData(data);
        } else {
          setErreur('Aucune donnée trouvée.');
          setLoading(false);
          return;
        }
      } catch {
        setErreur('Erreur lors du chargement.');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    // Vérifier que la classe a bien des étudiants — refuser d'imprimer un PV
    // vide (le user obtiendrait sinon un document sans nom, ni statistiques
    // significatives).
    if (!data.etudiants || data.etudiants.length === 0) {
      setErreur(`La classe ${classeCode} n'a aucun étudiant. Impression annulée.`);
      return;
    }

    // On passe l'ISO directement aux builders pour qu'ils puissent l'utiliser
    // soit comme Date (modèle ministériel → "deux mil vingt-six", "le 19 juin"),
    // soit comme libellé formaté en français (autres modèles).
    const html = buildPVByModele(data, dateDeliberation || '', modele, pvOptions);
    const w = window.open('', '_blank');
    if (!w) { setErreur("Impossible d'ouvrir la fenêtre. Vérifiez les paramètres de popup."); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 600);

    // Enregistrer l'impression
    try {
      await enregistrerPV({
        classe_code: classeCode,
        option_nom: data.info_classe.option_nom,
        pole_nom: data.info_classe.pole_nom,
        parcours_nom: data.info_classe.parcours_nom,
        session,
        semestre,
        annee_academique: anneeAcademique,
        date_deliberation: dateDeliberation || null,
        effectif_total: data.statistiques.effectif_total,
        nombre_admis: data.statistiques.nombre_admis,
        nombre_ajournes: data.statistiques.nombre_ajournes,
        taux_reussite: data.statistiques.taux_reussite,
        jury_snapshot: data.jury,
      });
      setSuccess('PV imprimé et enregistré dans l\'historique.');
    } catch {
      // Impression lancée même si l'enregistrement échoue
    }
  };

  // ── Export Word (single) ─────────────────────────────────────────────────
  // Reprend le HTML PV exact généré par buildPVByModele et l'encapsule dans
  // un document Word HTML (extension .doc). Word 2007+ ouvre le fichier avec
  // la même mise en page que la prévisualisation navigateur.
  const handleExporterWord = async () => {
    setErreur('');
    setSuccess('');

    let data = pvData;
    if (!data) {
      if (!classeCode) { setErreur('Veuillez sélectionner une classe.'); return; }
      setLoading(true);
      try {
        const result = await fetchDonneesPV({
          classe_code: classeCode,
          semestre,
          annee_academique: anneeAcademique,
          session,
        });
        if (result.data) { data = result.data; setPvData(data); }
        else { setErreur('Aucune donnée trouvée.'); setLoading(false); return; }
      } catch { setErreur('Erreur lors du chargement.'); setLoading(false); return; }
      setLoading(false);
    }

    if (!data.etudiants || data.etudiants.length === 0) {
      setErreur(`La classe ${classeCode} n'a aucun étudiant. Export annulé.`);
      return;
    }

    const html = buildPVByModele(data, dateDeliberation || '', modele, pvOptions);
    const filename = buildPVFilename(classeCode, semestre, anneeAcademique, session, dateDeliberation);
    await exportPVAsWord(html, filename);
    setSuccess(`Document Word téléchargé : ${filename}.doc`);
  };

  // ── Classes éligibles pour le mode batch ──
  // Filtré par pôle, niveau et type formation (FI = code sans -CS, CS = avec).
  const classesBatch = useMemo(() => {
    return classes.filter((c) => {
      if (batchPole && c.pole_nom !== batchPole) return false;
      if (batchNiveau && c.niveau !== batchNiveau) return false;
      const isCS = (c.code || '').endsWith('-CS');
      if (batchFormation === 'CS' && !isCS) return false;
      if (batchFormation === 'FI' && isCS) return false;
      return true;
    });
  }, [classes, batchPole, batchNiveau, batchFormation]);

  // ── Imprimer toutes les classes du filtre ──
  const handleImprimerBatch = async () => {
    if (classesBatch.length === 0) {
      setErreur('Aucune classe ne correspond aux filtres.');
      return;
    }
    setErreur('');
    setSuccess('');
    setBatchProgress({ done: 0, total: classesBatch.length });

    // ISO transmis au builder ; il choisit son rendu (lettres FR pour le
    // modèle ministériel, "12 février 2026" pour les autres).
    const dateStr = dateDeliberation || '';

    const htmls: string[] = [];
    const enregistrements: Promise<unknown>[] = [];
    let echecs = 0;
    let classesVides = 0;

    for (let i = 0; i < classesBatch.length; i++) {
      const cls = classesBatch[i];
      setBatchProgress({ done: i, total: classesBatch.length, classeEnCours: cls.code });
      try {
        const result = await fetchDonneesPV({
          classe_code: cls.code,
          semestre,
          annee_academique: anneeAcademique,
          session,
        });
        if (!result.data) { echecs += 1; continue; }
        const data = result.data;
        // Skip les options SANS étudiant — aucun PV à imprimer, ça encombrerait
        // le lot d'impression avec une page vide.
        if (!data.etudiants || data.etudiants.length === 0) {
          classesVides += 1;
          continue;
        }
        const html = buildPVByModele(data, dateStr, modele, pvOptions);
        htmls.push(html);
        // Trace l'impression — pas bloquant
        enregistrements.push(
          enregistrerPV({
            classe_code: cls.code,
            option_nom: data.info_classe.option_nom,
            pole_nom: data.info_classe.pole_nom,
            parcours_nom: data.info_classe.parcours_nom,
            session,
            semestre,
            annee_academique: anneeAcademique,
            date_deliberation: dateDeliberation || null,
            effectif_total: data.statistiques.effectif_total,
            nombre_admis: data.statistiques.nombre_admis,
            nombre_ajournes: data.statistiques.nombre_ajournes,
            taux_reussite: data.statistiques.taux_reussite,
            jury_snapshot: data.jury,
          }).catch(() => null),
        );
      } catch {
        echecs += 1;
      }
    }

    setBatchProgress({ done: classesBatch.length, total: classesBatch.length });

    if (htmls.length === 0) {
      setErreur(
        classesVides > 0
          ? `Aucun PV généré : ${classesVides} classe(s) sans étudiant ont été ignorée(s). Vérifiez que les étudiants sont bien rattachés à leur classe.`
          : 'Aucun PV n\'a pu être généré (vérifiez les données du semestre/session).'
      );
      setBatchProgress(null);
      return;
    }

    const combinedHtml = buildPVBatchHTML(htmls);
    const w = window.open('', '_blank');
    if (!w) {
      setErreur("Impossible d'ouvrir la fenêtre. Vérifiez les paramètres de popup.");
      setBatchProgress(null);
      return;
    }
    w.document.write(combinedHtml);
    w.document.close();
    setTimeout(() => { w.print(); }, 800);

    // On laisse les enregistrements se terminer en arrière-plan
    Promise.allSettled(enregistrements).then(() => {});

    const parts: string[] = [`${htmls.length} PV générés`];
    if (classesVides > 0) parts.push(`${classesVides} classe(s) vide(s) ignorée(s)`);
    if (echecs > 0) parts.push(`${echecs} en échec`);
    setSuccess(
      `${parts.join(' · ')}. La fenêtre d'impression contient tous les PV séparés par un saut de page.`,
    );
    setBatchProgress(null);
  };

  // ── Export Word batch : toutes les classes du filtre en 1 fichier .doc ──
  const handleExporterWordBatch = async () => {
    if (classesBatch.length === 0) {
      setErreur('Aucune classe ne correspond aux filtres.');
      return;
    }
    setErreur('');
    setSuccess('');
    setBatchProgress({ done: 0, total: classesBatch.length });

    const dateStr = dateDeliberation || '';
    const htmls: string[] = [];
    let echecs = 0;
    let classesVides = 0;

    for (let i = 0; i < classesBatch.length; i++) {
      const cls = classesBatch[i];
      setBatchProgress({ done: i, total: classesBatch.length, classeEnCours: cls.code });
      try {
        const result = await fetchDonneesPV({
          classe_code: cls.code,
          semestre,
          annee_academique: anneeAcademique,
          session,
        });
        if (!result.data) { echecs += 1; continue; }
        const data = result.data;
        if (!data.etudiants || data.etudiants.length === 0) { classesVides += 1; continue; }
        htmls.push(buildPVByModele(data, dateStr, modele, pvOptions));
      } catch { echecs += 1; }
    }

    setBatchProgress({ done: classesBatch.length, total: classesBatch.length });

    if (htmls.length === 0) {
      setErreur(classesVides > 0
        ? `Aucun PV généré : ${classesVides} classe(s) sans étudiant.`
        : `Aucun PV n'a pu être généré.`);
      setBatchProgress(null);
      return;
    }

    // Concaténation des HTML avec saut de page Word entre chaque PV
    const combined = htmls
      .map((h, idx) => {
        const bodyMatch = h.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        const body = bodyMatch ? bodyMatch[1] : h;
        const pageBreak = idx > 0
          ? `<br clear="all" style="mso-special-character:line-break;page-break-before:always;">`
          : '';
        return `${pageBreak}${body}`;
      })
      .join('\n');
    // On envoie le tout comme un unique HTML fictif au wrapper (qui pique le
    // <body>, ou à défaut prend le contenu tel quel).
    const filenameBase = `PV_batch_${classesBatch.length}classes_S${semestre}_${anneeAcademique}_${session}`;
    await exportPVAsWord(`<html><body>${combined}</body></html>`, filenameBase);
    setBatchProgress(null);

    const parts: string[] = [`${htmls.length} PV exportés dans un .doc unique`];
    if (classesVides > 0) parts.push(`${classesVides} classe(s) vide(s) ignorée(s)`);
    if (echecs > 0) parts.push(`${echecs} en échec`);
    setSuccess(parts.join(' · '));
  };

  // ── Jury CRUD ──
  const openJuryForm = (membre?: MembreJury) => {
    if (membre) {
      setEditingJury(membre);
      setJuryForm({
        nom_complet: membre.nom_complet,
        fonction: membre.fonction,
        titre_academique: membre.titre_academique,
        pole: membre.pole,
        ordre_affichage: membre.ordre_affichage,
        actif: membre.actif,
      });
    } else {
      setEditingJury(null);
      setJuryForm({
        nom_complet: '', fonction: 'membre', titre_academique: '',
        pole: poleId ?? 0, ordre_affichage: membresJury.length + 1, actif: true,
      });
    }
    setShowJuryForm(true);
  };

  const handleSaveJury = async () => {
    if (!juryForm.nom_complet.trim()) { setErreur('Le nom du membre est requis.'); return; }
    try {
      if (editingJury) {
        await updateMembreJury({ id: editingJury.id, data: juryForm }).unwrap();
      } else {
        await createMembreJury(juryForm).unwrap();
      }
      setShowJuryForm(false);
      setEditingJury(null);
    } catch {
      setErreur('Erreur lors de la sauvegarde du membre de jury.');
    }
  };

  const handleDeleteJury = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteMembreJury(deleteConfirm.id).unwrap();
      setDeleteConfirm(null);
    } catch {
      setErreur('Erreur lors de la suppression.');
    }
  };

  // ISO directement — chaque builder se charge d'afficher en français.
  const datePVLabel = dateDeliberation || '';

  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* Titre */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ClipboardCheck size={24} /> Procès-Verbaux de Délibération
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Impression des PV de délibération par classe — Gestion du jury
          </p>
        </div>
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-6">
          {([
            { id: 'generation' as const, label: 'Génération PV', icon: <Printer size={16} /> },
            { id: 'bottin' as const, label: 'Bottin finalistes', icon: <Users size={16} /> },
            { id: 'bottin_reinsc' as const, label: 'Bottin réinscription', icon: <Users size={16} /> },
            { id: 'historique' as const, label: 'Historique', icon: <Clock size={16} /> },
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ═══════════════ ONGLET GÉNÉRATION PV ═══════════════ */}
      {activeTab === 'generation' && (
        <div className="space-y-5">

          {/* Messages */}
          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm flex items-center justify-between">
              {erreur}
              <button onClick={() => setErreur('')}><X size={14} /></button>
            </div>
          )}
          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-700 dark:text-green-300 text-sm flex items-center justify-between">
              <span className="flex items-center gap-2"><CheckCircle size={14} /> {success}</span>
              <button onClick={() => setSuccess('')}><X size={14} /></button>
            </div>
          )}

          {/* Paramètres */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Paramètres du PV</h3>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Année académique */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Année académique</label>
                <select
                  value={anneeAcademique}
                  onChange={e => { setAnneeAcademique(e.target.value); setPvData(null); }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {annees.length === 0 && <option value="2024-2025">2024-2025</option>}
                  {annees.map(a => (
                    <option key={a.id} value={a.libelle}>{a.libelle}{a.est_active ? ' (active)' : ''}</option>
                  ))}
                </select>
              </div>

              {/* Semestre */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Semestre</label>
                <div className="flex gap-3 mt-1">
                  {([1, 2] as const).map(s => (
                    <label key={s} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={semestre === s} onChange={() => { setSemestre(s); setPvData(null); }} className="text-blue-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-200">Semestre {s}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Session */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Session</label>
                <div className="flex flex-wrap gap-3 mt-1">
                  {([
                    { val: 'ordinaire' as const, label: 'Ordinaire',
                      hint: 'Moyennes session 1 uniquement' },
                    { val: 'rattrapage' as const, label: 'Rattrapage',
                      hint: 'Uniquement les rattrapeurs, moyennes finales' },
                    { val: 'synthese' as const, label: 'Synthèse (bi-session)',
                      hint: 'Les deux sessions côte-à-côte — PV global' },
                  ]).map(opt => (
                    <label key={opt.val} className="flex items-center gap-2 cursor-pointer" title={opt.hint}>
                      <input type="radio" checked={session === opt.val}
                        onChange={() => { setSession(opt.val); setPvData(null); }}
                        className="text-blue-600" />
                      <span className="text-sm text-gray-700 dark:text-gray-200">{opt.label}</span>
                    </label>
                  ))}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                  {session === 'ordinaire' && 'Moyennes recomposées avant rattrapage · admis = ceux validés en SO'}
                  {session === 'rattrapage' && 'Seuls les étudiants ayant composé au rattrapage · moyennes finales'}
                  {session === 'synthese' && 'PV global — comparaison SO vs finale pour le ministère'}
                </div>
              </div>

              {/* Classe */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Classe {batchMode && <span className="text-amber-600 dark:text-amber-400 normal-case">(désactivé en mode batch)</span>}
                </label>
                <select
                  value={classeCode}
                  onChange={e => { setClasseCode(e.target.value); setPvData(null); }}
                  disabled={batchMode}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">-- Sélectionner --</option>
                  {classes.map(c => (
                    <option key={c.id} value={c.code}>{c.code} — {c.option_nom}</option>
                  ))}
                </select>
              </div>

              {/* Date de délibération */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date de délibération</label>
                <input
                  type="date"
                  value={dateDeliberation}
                  onChange={e => setDateDeliberation(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* ── Mode batch : imprimer plusieurs classes en un seul PDF ── */}
            <div className={`mt-4 p-4 rounded-lg border ${
              batchMode
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
                : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
            }`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={batchMode}
                  onChange={(e) => setBatchMode(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Layers size={14} className="text-amber-600 dark:text-amber-400" />
                    Imprimer toutes les classes en lot
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Génère un seul document avec un PV par classe (saut de page entre chaque), filtrable par pôle / niveau / type formation.
                    L'historique conserve une trace pour chaque classe imprimée.
                  </div>
                </div>
              </label>

              {batchMode && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pôle</label>
                    <select
                      value={batchPole}
                      onChange={(e) => setBatchPole(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Tous les pôles</option>
                      {poles.map((p) => (
                        <option key={p.id} value={p.nom}>{p.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Niveau</label>
                    <select
                      value={batchNiveau}
                      onChange={(e) => setBatchNiveau(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Tous les niveaux</option>
                      <option value="L1">L1</option>
                      <option value="L2">L2</option>
                      <option value="L3">L3</option>
                      <option value="M1">M1</option>
                      <option value="M2">M2</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Formation</label>
                    <select
                      value={batchFormation}
                      onChange={(e) => setBatchFormation(e.target.value as '' | 'FI' | 'CS')}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">FI + CS</option>
                      <option value="FI">FI uniquement</option>
                      <option value="CS">CS uniquement</option>
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <div className="text-xs text-gray-700 dark:text-gray-300">
                      <span className="font-semibold text-amber-700 dark:text-amber-400">{classesBatch.length}</span>{' '}
                      classe{classesBatch.length > 1 ? 's' : ''} sélectionnée{classesBatch.length > 1 ? 's' : ''}
                      {classesBatch.length > 0 && classesBatch.length <= 12 && (
                        <span className="ml-1 text-gray-500 dark:text-gray-400">
                          : {classesBatch.map((c) => c.code).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {batchProgress && (
                <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-amber-300 dark:border-amber-700">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <Loader2 size={12} className="animate-spin text-amber-600" />
                      Traitement : {batchProgress.done}/{batchProgress.total}
                      {batchProgress.classeEnCours && <span className="text-gray-500"> ({batchProgress.classeEnCours})</span>}
                    </span>
                    <span className="text-gray-500">{Math.round((batchProgress.done / batchProgress.total) * 100)}%</span>
                  </div>
                  <div className="mt-2 h-2 w-full bg-gray-200 dark:bg-gray-700 rounded">
                    <div
                      className="h-2 bg-amber-500 rounded transition-all"
                      style={{ width: `${(batchProgress.done / batchProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modèle de PV — filtré selon la session choisie.
                Session synthese → uniquement le modèle bi-session.
                Autres sessions → tous sauf bi-session. */}
            <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg">
              <label className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-2">Modèle de PV</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PV_MODELES
                  .filter(m => session === 'synthese'
                    ? m.value === 'synthese_bi_session'
                    : m.value !== 'synthese_bi_session')
                  .map(m => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setModele(m.value)}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      modele === m.value
                        ? 'border-indigo-500 bg-white dark:bg-gray-800 shadow-sm'
                        : 'border-transparent bg-white/50 dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className={`text-sm font-semibold ${modele === m.value ? 'text-indigo-700 dark:text-indigo-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {m.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{m.description}</div>
                  </button>
                ))}
              </div>

              {/* Salle du jury — visible pour les modèles qui l'utilisent */}
              {(modele === 'jury_classique' || modele === 'synthese_valides' || modele === 'ministeriel') && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Salle du jury</label>
                  <input
                    type="text"
                    value={salleJury}
                    onChange={e => setSalleJury(e.target.value)}
                    placeholder="Ex: HICHIKAWA"
                    className="w-full md:w-64 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}

              {/* Champs spécifiques au modèle ministériel */}
              {modele === 'ministeriel' && (
                <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-700/50">
                  <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-3">
                    Informations administratives (modèle ministériel)
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">N° du jury</label>
                      <input
                        type="text"
                        value={juryNumero}
                        onChange={e => setJuryNumero(e.target.value)}
                        placeholder="Ex: 03"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">N° note de service</label>
                      <input
                        type="text"
                        value={noteServiceNumero}
                        onChange={e => setNoteServiceNumero(e.target.value)}
                        placeholder="Ex: 125/MES/2026"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date note de service</label>
                      <input
                        type="date"
                        value={noteServiceDate}
                        onChange={e => setNoteServiceDate(e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ville (Fait à)</label>
                      <input
                        type="text"
                        value={ville}
                        onChange={e => setVille(e.target.value)}
                        placeholder="Pointe-Noire"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nationalité par défaut</label>
                      <input
                        type="text"
                        value={nationaliteParDefaut}
                        onChange={e => setNationaliteParDefaut(e.target.value)}
                        placeholder="Congolaise"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Mois de la session d'examens
                        {!moisSessionTouched && moisSession && (
                          <span className="ml-1 text-[10px] text-emerald-600 dark:text-emerald-400 normal-case">(auto)</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={moisSession}
                        onChange={e => { setMoisSession(e.target.value); setMoisSessionTouched(true); }}
                        placeholder="avril, septembre…"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                      {moisSessionTouched && (
                        <button
                          type="button"
                          onClick={() => setMoisSessionTouched(false)}
                          className="mt-1 text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Réinitialiser depuis l'année académique
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                    Les champs laissés vides apparaîtront en pointillés sur le PV pour être complétés à la main.
                    Le mois de session est dérivé automatiquement de la date d'examens / rattrapage configurée dans l'année académique.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Section Jury */}
          {classeCode && poleId && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Users size={16} /> Membres du Jury
                  <span className="text-xs font-normal text-gray-400">
                    ({membresJury.length} membre{membresJury.length !== 1 ? 's' : ''} pour ce pôle)
                  </span>
                </h3>
                <button
                  onClick={() => openJuryForm()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                >
                  <Plus size={14} /> Ajouter
                </button>
              </div>

              {membresJury.length === 0 ? (
                <div className="text-center py-6 text-gray-400 dark:text-gray-500 text-sm">
                  Aucun membre de jury configuré pour ce pôle.
                  <br />
                  <button onClick={() => openJuryForm()} className="text-blue-500 hover:underline mt-1 inline-block">
                    Ajouter le premier membre
                  </button>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      {['N°', 'Nom complet', 'Fonction', 'Titre', 'Ordre', 'Actions'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {membresJury.map((m, i) => (
                      <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{i + 1}</td>
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                          {m.titre_academique ? `${m.titre_academique} ` : ''}{m.nom_complet}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{m.fonction_display}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{m.titre_academique || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{m.ordre_affichage}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <button
                              onClick={() => openJuryForm(m)}
                              className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                              title="Modifier"
                            >
                              <Edit3 size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ id: m.id, nom: m.nom_complet })}
                              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                              title="Supprimer"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Résumé des données chargées */}
          {pvData && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <span className="font-semibold">{pvData.info_classe.classe_code}</span> — S{pvData.semestre} — Session {pvData.session === 'rattrapage' ? 'Rattrapage' : 'Ordinaire'}
                  <span className="mx-2">|</span>
                  <span className="font-medium">{pvData.etudiants.length}</span> étudiants
                  <span className="mx-2">|</span>
                  Admis : <span className="font-medium text-green-700 dark:text-green-400">{pvData.statistiques.nombre_admis}</span>
                  <span className="mx-2">|</span>
                  Ajournés : <span className="font-medium text-red-700 dark:text-red-400">{pvData.statistiques.nombre_ajournes}</span>
                  <span className="mx-2">|</span>
                  Taux : <span className="font-medium">{pvData.statistiques.taux_reussite.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            {!batchMode ? (
              <>
                <button
                  onClick={handleCharger}
                  disabled={loading || !classeCode}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm font-medium"
                >
                  {loading ? 'Chargement...' : 'Charger les données'}
                </button>
                <button
                  onClick={handlePrevisualiser}
                  disabled={loading || !classeCode}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                >
                  <Eye size={16} /> Prévisualiser
                </button>
                <button
                  onClick={handleImprimer}
                  disabled={loading || !classeCode}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                >
                  <Printer size={16} /> Imprimer le PV
                </button>
                <button
                  onClick={handleExporterWord}
                  disabled={loading || !classeCode}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                  title="Télécharger le PV au format Word (.doc)"
                >
                  <FileType2 size={16} /> Exporter Word
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleImprimerBatch}
                  disabled={!!batchProgress || classesBatch.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
                >
                  {batchProgress
                    ? <><Loader2 size={16} className="animate-spin" /> Génération en cours…</>
                    : <><Layers size={16} /> Imprimer toutes les classes ({classesBatch.length})</>}
                </button>
                <button
                  onClick={handleExporterWordBatch}
                  disabled={!!batchProgress || classesBatch.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                  title="Télécharger toutes les classes dans un unique fichier Word"
                >
                  <FileType2 size={16} /> Exporter Word ({classesBatch.length})
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ ONGLET BOTTIN FINALISTES ═══════════════ */}
      {activeTab === 'bottin' && (
        <BottinTab annees={annees.map(a => a.libelle)} defaultAnnee={anneeAcademique} />
      )}

      {activeTab === 'bottin_reinsc' && (
        <BottinReinscriptionTab annees={annees.map(a => a.libelle)} defaultAnnee={anneeAcademique} />
      )}

      {/* ═══════════════ ONGLET HISTORIQUE ═══════════════ */}
      {activeTab === 'historique' && (
        <div className="space-y-5">

          {/* Stats cards */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: 'Total PV', value: historique?.total ?? 0, color: 'blue', icon: <FileText size={20} /> },
              { label: 'Ce mois', value: historique?.ce_mois ?? 0, color: 'indigo', icon: <Clock size={20} /> },
              { label: 'Ordinaires', value: historique?.ordinaires ?? 0, color: 'amber', icon: <ClipboardCheck size={20} /> },
              { label: 'Rattrapages', value: historique?.rattrapages ?? 0, color: 'green', icon: <CheckCircle size={20} /> },
            ].map(card => (
              <div key={card.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{card.label}</span>
                  <span className={`text-${card.color}-500`}>{card.icon}</span>
                </div>
                <div className={`text-2xl font-bold text-${card.color}-600 dark:text-${card.color}-400`}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {/* Filtres */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex gap-3 flex-wrap">
              <input type="text" value={histAnnee} onChange={e => setHistAnnee(e.target.value)}
                placeholder="Année (ex: 2024-2025)"
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-40" />
              <select value={histSession} onChange={e => setHistSession(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">Toutes sessions</option>
                <option value="ordinaire">Ordinaire</option>
                <option value="rattrapage">Rattrapage</option>
              </select>
              <input type="date" value={histDateDebut} onChange={e => setHistDateDebut(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              <input type="date" value={histDateFin} onChange={e => setHistDateFin(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
          </div>

          {/* Table historique */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Date', 'Classe', 'Session', 'Semestre', 'Effectif', 'Admis', 'Ajournés', 'Taux', 'Imprimé par'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(historique?.pv_list ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                      Aucun PV enregistré
                    </td>
                  </tr>
                ) : (historique?.pv_list ?? []).map(pv => (
                  <tr key={pv.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {new Date(pv.date_impression).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{pv.classe_code}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        pv.session === 'rattrapage'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>
                        {pv.session_display}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">S{pv.semestre}</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">{pv.effectif_total}</td>
                    <td className="px-4 py-3 text-center font-medium text-green-600 dark:text-green-400">{pv.nombre_admis}</td>
                    <td className="px-4 py-3 text-center font-medium text-red-600 dark:text-red-400">{pv.nombre_ajournes}</td>
                    <td className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">{pv.taux_reussite}%</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{pv.imprime_par_nom ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL PRÉVISUALISATION PV ═══════════════ */}
      {showPreview && pvData && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto">
          <div className="my-4 w-full max-w-7xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-t-xl">
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Aperçu PV — {pvData.info_classe.classe_code} — S{pvData.semestre} — {pvData.session === 'rattrapage' ? 'Rattrapage' : 'Ordinaire'}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleImprimer}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium"
                >
                  <Printer size={14} /> Imprimer
                </button>
                <button
                  onClick={handleExporterWord}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs font-medium"
                  title="Télécharger au format Word"
                >
                  <FileType2 size={14} /> Word
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-[85vh]">
              <PVPreview data={pvData} dateDeliberation={datePVLabel} modele={modele} options={pvOptions} />
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL FORMULAIRE JURY ═══════════════ */}
      {showJuryForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                {editingJury ? 'Modifier le membre' : 'Nouveau membre de jury'}
              </h3>
              <button onClick={() => setShowJuryForm(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nom complet *</label>
                <input
                  type="text"
                  value={juryForm.nom_complet}
                  onChange={e => setJuryForm({ ...juryForm, nom_complet: e.target.value })}
                  placeholder="Ex: MAKOSSO Jean-Pierre"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Titre académique</label>
                <input
                  type="text"
                  value={juryForm.titre_academique}
                  onChange={e => setJuryForm({ ...juryForm, titre_academique: e.target.value })}
                  placeholder="Ex: Pr, Dr, M., Mme"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Fonction *</label>
                <select
                  value={juryForm.fonction}
                  onChange={e => setJuryForm({ ...juryForm, fonction: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {FONCTIONS.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ordre d'affichage</label>
                <input
                  type="number"
                  min="0"
                  value={juryForm.ordre_affichage}
                  onChange={e => setJuryForm({ ...juryForm, ordre_affichage: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={juryForm.actif}
                  onChange={e => setJuryForm({ ...juryForm, actif: e.target.checked })}
                  className="rounded text-blue-600"
                />
                <label className="text-sm text-gray-700 dark:text-gray-300">Actif</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowJuryForm(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveJury}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                <Save size={14} /> {editingJury ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ MODAL CONFIRMATION SUPPRESSION JURY ═══════════════ */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <AlertTriangle size={24} className="text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Supprimer ce membre ?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              <span className="font-medium">{deleteConfirm.nom}</span> sera retiré du jury.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteJury}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PVDeliberation;

// ═══════════════════════════════════════════════════════════════════════════
// Onglet Bottin des étudiants finalistes (document ministériel)
// ═══════════════════════════════════════════════════════════════════════════

interface BottinTabProps {
  annees: string[];
  defaultAnnee: string;
}

const BottinTab: React.FC<BottinTabProps> = ({ annees, defaultAnnee }) => {
  const [anneeBottin, setAnneeBottin] = useState(defaultAnnee);
  const [campus, setCampus] = useState('Pointe-Noire');
  const [responsable, setResponsable] = useState('');
  const [dateEmission, setDateEmission] = useState('');
  const [nationaliteParDefaut, setNationaliteParDefaut] = useState('Congolaise');

  const [fetchBottin, { data: bottinData, isFetching, error }]
    = useLazyGetDonneesBottinQuery();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const handleCharger = useCallback(async () => {
    setFeedback(null);
    try {
      await fetchBottin({
        annee_academique: anneeBottin,
        cycle: 'licence',
        campus,
        responsable_academique: responsable,
      }).unwrap();
    } catch (e: any) {
      setFeedback(e?.data?.error || 'Erreur de chargement');
    }
  }, [fetchBottin, anneeBottin, campus, responsable]);

  const buildHtml = useCallback((data: DonneesBottin): string => {
    return buildBottinHTML(data, {
      ville: campus,
      nationaliteParDefaut,
      dateEmission,
    });
  }, [campus, nationaliteParDefaut, dateEmission]);

  const handleApercu = () => {
    if (!bottinData) return;
    setPreviewHtml(buildHtml(bottinData));
    setPreviewOpen(true);
  };

  const handleImprimer = () => {
    if (!bottinData) return;
    const html = buildHtml(bottinData);
    const w = window.open('', '_blank');
    if (!w) { alert('Le navigateur a bloqué l\'ouverture. Autorisez les popups.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Impression automatique dès le chargement
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* noop */ } }, 400);
  };

  // Analyse rapide : combien d'étudiants ont fait leurs 3 années à 2i vs partiel
  const stats = React.useMemo(() => {
    if (!bottinData) return null;
    const total = bottinData.etudiants.length;
    const complets = bottinData.etudiants.filter(e => e.nb_semestres_2i === 6).length;
    const partiels = total - complets;
    return { total, complets, partiels };
  }, [bottinData]);

  return (
    <div className="space-y-5">
      {/* Bandeau info du modèle */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-3 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <FileText size={18} className="text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="text-sm">
          <p className="font-semibold text-indigo-800 dark:text-indigo-300">
            Bottin des étudiants finalistes (document ministériel)
          </p>
          <p className="text-xs text-indigo-700 dark:text-indigo-400 mt-1">
            Liste des finalistes L3 avec leurs moyennes S1 à S6, format A4 paysage.
            Les moyennes des années antérieures sont récupérées automatiquement
            depuis les schémas historiques par matching nom+prénom (résilient
            aux changements de matricule).
          </p>
        </div>
      </div>

      {/* Paramètres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Année académique */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Année académique
            </label>
            <select value={anneeBottin} onChange={e => setAnneeBottin(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              {annees.length === 0 && <option>{defaultAnnee}</option>}
              {annees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              Les schémas historiques (N-1 et N-2) sont scannés pour les S1 à S4.
            </p>
          </div>
          {/* Campus */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Campus
            </label>
            <select value={campus} onChange={e => setCampus(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              <option>Pointe-Noire</option>
              <option>Brazzaville</option>
            </select>
          </div>
          {/* Cycle (fixe pour l'instant) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Cycle
            </label>
            <select disabled value="licence"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-500 cursor-not-allowed">
              <option value="licence">LICENCE (L3)</option>
            </select>
            <p className="text-[10px] text-gray-500 mt-1">Master (M2) — à venir.</p>
          </div>
          {/* Date d'émission */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Date d'émission (ligne « Fait à … »)
            </label>
            <input type="date" value={dateEmission}
              onChange={e => setDateEmission(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Responsable académique */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Responsable académique (DAAC)
            </label>
            <input type="text" value={responsable}
              onChange={e => setResponsable(e.target.value)}
              placeholder="Nom complet"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            <p className="text-[10px] text-gray-500 mt-1">
              Apparaît dans « 1. Informations générales » et dans l'attestation finale.
            </p>
          </div>
          {/* Nationalité par défaut */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Nationalité par défaut
            </label>
            <input type="text" value={nationaliteParDefaut}
              onChange={e => setNationaliteParDefaut(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            <p className="text-[10px] text-gray-500 mt-1">
              Appliquée à tous les étudiants (la nationalité n'est pas stockée dans le dossier étudiant).
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4">
          <button onClick={handleCharger} disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50">
            {isFetching ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
            {isFetching ? 'Chargement…' : 'Charger les finalistes'}
          </button>
          {bottinData && (
            <>
              <button onClick={handleApercu}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 text-sm font-medium rounded-lg">
                <Eye size={16} /> Aperçu
              </button>
              <button onClick={handleImprimer}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg">
                <Printer size={16} /> Imprimer
              </button>
            </>
          )}
          {feedback && (
            <span className="text-xs text-red-600 dark:text-red-400 ml-2">{feedback}</span>
          )}
          {(error as any) && (
            <span className="text-xs text-red-600 dark:text-red-400 ml-2">
              {(error as any)?.data?.error || 'Erreur de chargement'}
            </span>
          )}
        </div>
      </div>

      {/* Aperçu résumé */}
      {bottinData && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <KpiCard label="Effectif total" value={bottinData.effectif_total} tone="blue" />
            <KpiCard label="Cursus complet (3 ans)" value={stats?.complets ?? 0} tone="green" />
            <KpiCard label="Cursus partiel" value={stats?.partiels ?? 0} tone="amber" />
            <KpiCard label="Filières" value={bottinData.recapitulatif_par_filiere.length} tone="indigo" />
          </div>

          {bottinData.schemas_historiques_utilises.length > 0 && (
            <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Schémas historiques scannés :{' '}
              {bottinData.schemas_historiques_utilises.map(a => (
                <span key={a} className="inline-block px-2 py-0.5 mr-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                  {a}
                </span>
              ))}
            </div>
          )}

          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-2">
            <Users size={14} /> Récapitulatif par filière
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 dark:border-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 border-b border-gray-200 dark:border-gray-600">Filière / Parcours</th>
                  <th className="text-center px-3 py-2 border-b border-gray-200 dark:border-gray-600">Niveau</th>
                  <th className="text-center px-3 py-2 border-b border-gray-200 dark:border-gray-600">Effectif</th>
                  <th className="text-center px-3 py-2 border-b border-gray-200 dark:border-gray-600">Dont filles</th>
                </tr>
              </thead>
              <tbody>
                {bottinData.recapitulatif_par_filiere.map((r, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-1.5">{r.parcours}</td>
                    <td className="px-3 py-1.5 text-center">{r.niveau}</td>
                    <td className="px-3 py-1.5 text-center font-medium">{r.effectif_total}</td>
                    <td className="px-3 py-1.5 text-center text-pink-600 dark:text-pink-400">{r.dont_filles}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mt-4 mb-2 flex items-center gap-2">
            <FileText size={14} /> Aperçu de la liste ({bottinData.etudiants.length} étudiants)
          </h3>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs border border-gray-200 dark:border-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">#</th>
                  <th className="text-left px-2 py-1.5">Nom Prénoms</th>
                  <th className="text-center px-2 py-1.5">Sexe</th>
                  <th className="text-left px-2 py-1.5">Parcours</th>
                  <th className="text-center px-2 py-1.5">Entrée</th>
                  <th className="text-center px-2 py-1.5">S1</th>
                  <th className="text-center px-2 py-1.5">S2</th>
                  <th className="text-center px-2 py-1.5">S3</th>
                  <th className="text-center px-2 py-1.5">S4</th>
                  <th className="text-center px-2 py-1.5">S5</th>
                  <th className="text-center px-2 py-1.5">S6</th>
                </tr>
              </thead>
              <tbody>
                {bottinData.etudiants.map((e, i) => (
                  <tr key={e.matricule || i} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="px-2 py-1">{i + 1}</td>
                    <td className="px-2 py-1"><strong>{e.nom}</strong> {e.prenoms}</td>
                    <td className="px-2 py-1 text-center">{e.sexe}</td>
                    <td className="px-2 py-1">{e.parcours}</td>
                    <td className="px-2 py-1 text-center text-gray-500">{e.annee_entree.split('-')[0]}</td>
                    <td className="px-2 py-1 text-center tabular-nums">{e.moyenne_s1?.toFixed(2) ?? '—'}</td>
                    <td className="px-2 py-1 text-center tabular-nums">{e.moyenne_s2?.toFixed(2) ?? '—'}</td>
                    <td className="px-2 py-1 text-center tabular-nums">{e.moyenne_s3?.toFixed(2) ?? '—'}</td>
                    <td className="px-2 py-1 text-center tabular-nums">{e.moyenne_s4?.toFixed(2) ?? '—'}</td>
                    <td className="px-2 py-1 text-center tabular-nums font-semibold">{e.moyenne_s5?.toFixed(2) ?? '—'}</td>
                    <td className="px-2 py-1 text-center tabular-nums font-semibold">{e.moyenne_s6?.toFixed(2) ?? '—'}</td>
                  </tr>
                ))}
                {bottinData.etudiants.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-500 italic">
                    Aucun étudiant finaliste pour {bottinData.annee_academique}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal aperçu HTML */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-800">Aperçu — Bottin {bottinData?.annee_academique}</h3>
              <div className="flex gap-2">
                <button onClick={handleImprimer}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded">
                  <Printer size={14} /> Imprimer
                </button>
                <button
                  onClick={async () => {
                    if (!previewHtml || !bottinData) return;
                    await exportPVAsWord(
                      previewHtml,
                      `Bottin_${(bottinData.annee_academique || '').replace(/[^0-9-]/g, '')}`,
                    );
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded"
                  title="Télécharger au format Word"
                >
                  <FileType2 size={14} /> Word
                </button>
                <button onClick={() => setPreviewOpen(false)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded">
                  <X size={14} /> Fermer
                </button>
              </div>
            </div>
            <iframe srcDoc={previewHtml} className="flex-1 w-full" title="Aperçu Bottin" />
          </div>
        </div>
      )}
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: number; tone: 'blue' | 'green' | 'amber' | 'indigo' }>
  = ({ label, value, tone }) => {
  const tones: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
  };
  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
};
